import { ModalBase } from "../ModalBase";
import { Wizard } from "components/wizards/Wizard";
import { useDispatch } from "react-redux";
import { SelectDestinationStep } from "./steps/SelectDestination";
import { PlatformSettings } from "./steps/PlatformSettings";
import { LivestreamDetailsStep } from "./steps/LivestreamDetails";
import { ProcessingStep } from "./steps/Processing";
import { DestinationDescriptor } from "views/page-content/platforms/platform/simulcast/types";
import { updateFormState, updateGeneralForm } from "store/platforms/slice";
import { useSpeedTest } from "hooks/useSpeedTest";
import { useNewLivestreamStepLogic } from "./useNewLivestreamStepLogic";
import { NewLivestreamModalSteps } from "./types";
import { useEffect } from "react";
import { ScheduledOptions } from "views/page-content/platforms/platform/forms/types";
import { useHasPlanRole } from "hooks/useHasPlanRole";

export const NewLivestreamModal = ({ reload }: { reload: () => void }) => {
    const dispatch = useDispatch();
    const isStudioUser = useHasPlanRole("Studio");
    const {
        isSubmitting,
        submissionAttemptId,
        isAttemptCancelled,
        setIsSubmittingForAttempt,
        setSubmissionStatusForAttempt,
        activeStep,
        setActiveStep,
        setSelectedPlatform,
        selectedDestinationId,
        setSelectedDestinationId,
        selectedPlatform,
        setHasAddedPlatformSpecificSettings,
        setCurrentFacebookEdge,
        validationErrors,
        setOnAdvanceStep,
        advanceStep,
        advanceButtonText,
        setOnDismiss,
        dismissButtonAction,
        dismissButtonText,
        canProceedToNextStep,
        modalHeaderText
    } = useNewLivestreamStepLogic({ reload });

    const { SpeedTestDisplay } = useSpeedTest({
        onCompleted: (results) => {
            dispatch(
                updateGeneralForm({
                    selectedQuality: results?.suggestedSetting
                })
            );
        }
    });

    // In addition to the destination specific details finalized in the processing step callback,
    // we also need to ensure some general form details are set correctly
    useEffect(() => {
        dispatch(updateFormState(ScheduledOptions.Later));
        dispatch(updateGeneralForm({ enableRecording: !isStudioUser }));
    }, [dispatch, isStudioUser]);

    return (
        <ModalBase
            isOpen
            header={modalHeaderText}
            successButton={advanceButtonText}
            onSuccess={advanceStep}
            dismissButton={dismissButtonText}
            onDismiss={dismissButtonAction}
            successDisabled={!canProceedToNextStep}
            shouldNotCloseOnSuccess={true}
        >
            <div>
                <Wizard<NewLivestreamModalSteps>
                    activeStep={activeStep}
                    steps={[
                        {
                            id: NewLivestreamModalSteps.SelectDestination,
                            component: (
                                <SelectDestinationStep
                                    setSelectedPlatform={setSelectedPlatform}
                                    advanceToPlatformSettings={(
                                        destination?: DestinationDescriptor
                                    ) => {
                                        if (destination) {
                                            setSelectedDestinationId(
                                                destination.id
                                            );
                                            setHasAddedPlatformSpecificSettings(
                                                true
                                            );
                                        }
                                        setActiveStep(
                                            NewLivestreamModalSteps.PlatformSettings
                                        );
                                    }}
                                />
                            )
                        },
                        {
                            id: NewLivestreamModalSteps.PlatformSettings,
                            component: (
                                <PlatformSettings
                                    selectedPlatform={selectedPlatform}
                                    setHasAddedPlatformSpecificSettings={
                                        setHasAddedPlatformSpecificSettings
                                    }
                                    setOnAdvanceStep={setOnAdvanceStep}
                                    selectedDestinationId={
                                        selectedDestinationId
                                    }
                                    advanceStep={advanceStep}
                                    setOnDismiss={setOnDismiss}
                                    setCurrentFacebookEdge={
                                        setCurrentFacebookEdge
                                    }
                                />
                            )
                        },
                        {
                            id: NewLivestreamModalSteps.LivestreamDetails,
                            component: (
                                <LivestreamDetailsStep
                                    validationErrors={validationErrors}
                                    setOnAdvanceStep={setOnAdvanceStep}
                                    SpeedTestDisplay={SpeedTestDisplay}
                                />
                            )
                        },
                        {
                            id: NewLivestreamModalSteps.Processing,
                            component: (
                                <ProcessingStep
                                    attemptId={submissionAttemptId}
                                    isSubmitting={isSubmitting}
                                    isCancelled={isAttemptCancelled}
                                    setIsSubmitting={setIsSubmittingForAttempt}
                                    setSubmissionStatus={
                                        setSubmissionStatusForAttempt
                                    }
                                    setOnAdvanceStep={setOnAdvanceStep}
                                />
                            )
                        }
                    ]}
                />
            </div>
        </ModalBase>
    );
};
